<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import SiteMapReactivateButton from '@/components/eos/site-map/SiteMapReactivateButton.vue'
import SiteMapSelectCheckbox from '@/components/eos/site-map/SiteMapSelectCheckbox.vue'
import { useActivationSelection } from '@/composables/eos/activation-selection'
import { useSiteMap } from '@/composables/eos/site-map'
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import { SiteType } from '@/types/core/Site'
import type { ISiteMapStructureFilters } from '@/types/eos/filters'
import type { ISiteMapStructure } from '@/types/eos/SiteMap'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Icon } from '@iconify/vue'
import { computed, reactive } from 'vue'

const filters = reactive<ISiteMapStructureFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  site_id: undefined,
  name: undefined,
})

const { getStructuresQuery, deactivateStructuresMutation, reactivateStructuresMutation } = useSiteMap()
const { data, isError, isPending, isRefetching, error, refetch } = getStructuresQuery(filters)

const rows = computed(() => data.value?.data)
const {
  selected,
  allSelected,
  partiallySelected,
  headerVariant,
  canDeactivate,
  canReactivate,
  isMixed,
  isSelected,
  toggleRow,
  cycleSelection,
  clearSelection,
} = useActivationSelection(rows)

const onDeactivate = () => {
  const count = selected.value.length
  DeleteConfirmationModal(
    'Deactivate structures',
    `Deactivate ${count} selected ${count === 1 ? 'structure' : 'structures'}? Their active racks will be deactivated too. They can be reactivated later.`,
    selected.value.map((row) => row.id),
    deactivateStructuresMutation.mutateAsync,
    clearSelection,
    'Deactivate'
  )
}

const onReactivate = async () => {
  try {
    await reactivateStructuresMutation.mutateAsync(selected.value.map((row) => row.id))
    clearSelection()
  } catch {
    // Errors are already notified by the mutation's onError callback.
  }
}

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id ?? undefined
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="flex gap-2">
      <RcSiteSelect
        placeholder="Site ID or Name"
        :type="SiteType.DataCenter"
        :disabled="isPending"
        style="width: 200px"
        @update:model-value="onSiteFilterUpdate"
      />
      <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isPending" />
      <div class="ml-auto flex items-center gap-2">
        <span v-if="isMixed" class="text-sm text-gray-500"
          >Select only active or only inactive rows to act on them</span
        >
        <SiteMapReactivateButton
          :disabled="!canReactivate"
          :loading="reactivateStructuresMutation.isPending.value"
          @click="onReactivate"
        >
          Reactivate{{ canReactivate ? ` (${selected.length})` : '' }}
        </SiteMapReactivateButton>
        <el-button
          type="danger"
          :disabled="!canDeactivate"
          :loading="deactivateStructuresMutation.isPending.value"
          @click="onDeactivate"
        >
          Deactivate{{ canDeactivate ? ` (${selected.length})` : '' }}
        </el-button>
      </div>
    </div>
    <el-table v-loading="isPending" :data="data?.data" stripe :empty-text="isPending ? 'Loading...' : 'No data.'">
      <el-table-column width="55">
        <template #header>
          <SiteMapSelectCheckbox
            :model-value="allSelected"
            :indeterminate="partiallySelected"
            :variant="headerVariant"
            :disabled="isPending || !rows?.length"
            @change="cycleSelection"
          />
        </template>
        <template #default="{ row }">
          <SiteMapSelectCheckbox
            :model-value="isSelected(row as ISiteMapStructure)"
            :variant="row.deactivated_at == null ? 'active' : 'inactive'"
            @change="(value) => toggleRow(row as ISiteMapStructure, value)"
          />
        </template>
      </el-table-column>
      <el-table-column type="expand">
        <template #default="scope">
          <el-table :data="scope.row.racks" empty-text="No data" size="small">
            <el-table-column label="Racks" header-align="center">
              <el-table-column prop="name" label="Name" />
              <el-table-column prop="operational" label="Operational">
                <template #default="scope">
                  <Icon v-if="scope.row.operational" icon="material-symbols:check" height="20" width="20" />
                  <Icon v-else icon="material-symbols:close" height="20" width="20" />
                </template>
              </el-table-column>
            </el-table-column>
          </el-table>
        </template>
      </el-table-column>
      <el-table-column label="Name" prop="name" :min-width="192" />
      <el-table-column label="Site" prop="site.name" :min-width="192" />
      <el-table-column label="Operational" prop="operational" :min-width="120">
        <template #default="scope">
          <Icon v-if="scope.row.operational" icon="material-symbols:check" height="20" width="20" />
          <Icon v-else icon="material-symbols:close" height="20" width="20" />
        </template>
      </el-table-column>
      <el-table-column label="Foreman ID" prop="foreman_id" :min-width="120" />
      <el-table-column label="Created At" prop="created_at" :min-width="120" :formatter="dateTimeFormatter" />
      <el-table-column label="Deactivated At" prop="deactivated_at" :min-width="120" :formatter="dateTimeFormatter" />
      <el-table-column label="Last Updated" prop="updated_at" :min-width="120" :formatter="dateTimeFormatter" />
    </el-table>
    <el-pagination
      v-if="data"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="flex justify-end"
      background
      :layout="DEFAULT_PAGINATION_LAYOUT"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      hide-on-single-page
    />
  </template>
</template>
